import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { profilesApi } from '@/api/profilesApi';
import type {
  AddAppToProfileRequest,
  CreateProfileRequest,
  ProfileListRequest,
  UpdateProfileAppRequest,
  UpdateProfileRequest,
} from '@/types/profile';

const KEYS = {
  all: ['profiles'] as const,
  list: (f: ProfileListRequest) => [...KEYS.all, 'list', f] as const,
  detail: (id: number) => [...KEYS.all, 'detail', id] as const,
};

export function useProfiles(filters: ProfileListRequest = {}) {
  return useQuery({ queryKey: KEYS.list(filters), queryFn: () => profilesApi.list(filters) });
}

export function useProfile(id: number | null) {
  return useQuery({
    queryKey: KEYS.detail(id ?? 0),
    queryFn: () => profilesApi.getById(id!),
    enabled: id != null,
  });
}

export function useCreateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (r: CreateProfileRequest) => profilesApi.create(r),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
}

export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, request }: { id: number; request: UpdateProfileRequest }) =>
      profilesApi.update(id, request),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
}

export function useDeactivateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => profilesApi.deactivate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
}

export function useAddAppToProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ profileId, request }: { profileId: number; request: AddAppToProfileRequest }) =>
      profilesApi.addApp(profileId, request),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: KEYS.all });
      qc.invalidateQueries({ queryKey: KEYS.detail(vars.profileId) });
    },
  });
}

export function useUpdateProfileApp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ profileId, profileAppId, request }: { profileId: number; profileAppId: number; request: UpdateProfileAppRequest }) =>
      profilesApi.updateApp(profileId, profileAppId, request),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: KEYS.detail(vars.profileId) });
    },
  });
}

export function useRemoveProfileApp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ profileId, profileAppId }: { profileId: number; profileAppId: number }) =>
      profilesApi.removeApp(profileId, profileAppId),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: KEYS.all });
      qc.invalidateQueries({ queryKey: KEYS.detail(vars.profileId) });
    },
  });
}
